import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Input, message, Card, Alert, Image } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  BarsOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import CSRForm from "./csrForm";
import dayjs from "dayjs";
import CommonPagination from "../../components/common/pagination";

const CSRPage = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchYear, setSearchYear] = useState("");
  const [sortYear, setSortYear] = useState();
  const [openDetail, setOpenDetail] = useState();
  const [detailData, setDetailData] = useState();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const fieldLabels = {
    year: "Year",
    title_id: "Title ID",
    title_en: "Title EN",
    description_id: "Description ID",
    description_en: "Description EN",
    image: "Image",
    created_at: "Created At",
    updated_at: "Updated At",
  };

  const detailOrder = [
    "year",
    "title_id",
    "title_en",
    "description_id",
    "description_en",
    "image",
    "created_at",
    "updated_at",
  ];

  const fetchData = async (
    pageNumber = 1,
    searchText = "",
    searchYear = "",
    sortYear = "desc",
  ) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/csr`, {
        params: {
          page: pageNumber,
          limit,
          year: searchYear,
          searchText,
          sortYear,
        },
      });
      console.log("API response:", res.data);
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      message.error("Failed to fetch CSR data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(page, searchText, searchYear, sortYear || "desc");
  }, [page, searchText, searchYear, sortYear]);

  const handleSubmit = async (formData) => {
    try {
      if (editData) {
        await axios.put(`${BASE_URL}/api/csr/${editData.csr_id}`, formData, {});
        setSuccessMsg("CSR updated successfully");
      } else {
        await axios.post(`${BASE_URL}/api/csr`, formData, {});
        setSuccessMsg("CSR created successfully");
      }
      setOpen(false);
      setEditData(null);
      fetchData();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      message.error("Failed to submit data");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/csr/${id}`);
      setSuccessMsg("CSR deleted");
      setTimeout(() => setSuccessMsg(null), 3000);
      fetchData(page);
    } catch (err) {
      message.error("Failed to delete");
    }
  };
  const columns = [
    {
      title: "No",
      render: (_, __, index) => (page - 1) * limit + index + 1,
    },
    {
      title: "Year",
      dataIndex: "year",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      onHeaderCell: (column) => ({
        onClick: () => {
          const newSort = sortYear === "asc" ? "desc" : "asc";
          setSortYear(newSort);
        },
      }),
    },
    { title: "Title ID", dataIndex: "title_id" },
    { title: "Title EN", dataIndex: "title_en" },
    {
      title: "Image",
      dataIndex: "image",
      render: (url) =>
        url ? (
          <img
            src={`${BASE_URL}${url}`}
            alt="csr"
            width="60"
            style={{ borderRadius: 8 }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <Button
            style={{ borderColor: "#ec4899", color: "#ec4899", marginRight: 8 }}
            icon={<BarsOutlined />}
            type="link"
            onClick={() => {
              setDetailData({ ...record });
              setOpenDetail(true);
            }}
          />

          <Button
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
              marginRight: 8,
            }}
            icon={<EditOutlined />}
            type="link"
            onClick={() => {
              setEditData(record);
              setOpen(true);
            }}
          />
          <Button
            style={{
              backgroundColor: "#374151",
              borderColr: "#374151",
              color: "#fff",
            }}
            icon={<DeleteOutlined />}
            type="link"
            onClick={() => {
              Modal.confirm({
                title: "Are you sure you want to delete this csr?",
                centered: true,
                okText: "Yes",
                okType: "danger",
                cancelText: "No",
                onOk: () => handleDelete(record.csr_id),
              });
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <Card title="CSR">
      {successMsg && (
        <Alert
          message={successMsg}
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Input.Search
          placeholder="Search CSR..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
        <Button
          style={{ backgroundColor: "#ec4899", color: "#fff" }}
          onClick={() => {
            setEditData(null);
            setOpen(true);
          }}
        >
          + Add CSR
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="csr_id"
        loading={loading}
        pagination={false}
        style={{ marginTop: 20 }}
      />

      <CommonPagination
        current={page}
        pageSize={limit}
        total={total}
        onChange={(newPage) => setPage(newPage)}
      />

      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditData(null);
        }}
        footer={null}
        forceRender
      >
        <CSRForm
          key={editData?.csr_id || "new"}
          initialValues={editData}
          onSubmit={handleSubmit}
        />
      </Modal>

      <Modal
        title="Awards Detail"
        open={openDetail}
        onCancel={() => setOpenDetail(false)}
        footer={[
          <Button
            key="close"
            style={{
              backgroundColor: "#ec4899",
              borderColor: "#ec4899",
              color: "#fff",
              marginRight: 8,
            }}
            onClick={() => setOpenDetail(false)}
          >
            Close
          </Button>,
        ]}
      >
        {detailData && (
          <Table
            dataSource={detailOrder.map((key) => {
              let value = detailData[key];
              if (key === "image") {
                if (value) {
                  const src = `${BASE_URL}${value}`;
                  value = (
                    <Image
                      src={src}
                      width={100}
                      style={{ borderRadius: 4 }}
                      preview={{
                        mask: (
                          <EyeOutlined
                            style={{ color: "white", fontSize: 18 }}
                          />
                        ),
                      }}
                    />
                  );
                } else {
                  value = "-";
                }
              }

              if ((key === "created_at" || key === "updated_at") && value) {
                value = dayjs(value).format("YYYY-MM-DD HH:mm:ss");
              }

              return {
                key,
                field: fieldLabels[key] || key,
                value,
              };
            })}
            columns={[
              {
                title: "Field",
                dataIndex: "field",
                key: "field",
                width: "30%",
                render: (text) => <strong>{text}</strong>,
              },
              { title: "Value", dataIndex: "value", key: "value" },
            ]}
            pagination={false}
            bordered
          />
        )}
      </Modal>
    </Card>
  );
};

export default CSRPage;
